import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { queryTillSuccess, toLocaleDecimal } from '../../common/utils';
import Spinner from '../../components/Spinner';
import { SpecificMEVTxDataResponse } from '../../types';
import './styles.scss';
import Mustache from 'mustache';

const txQuery = `WITH 

prices AS (
  SELECT
  	TRUNC(recorded_at, 'day') as date,
  	AVG(price) as price
  FROM
  	osmosis.core.dim_prices
  WHERE
  	symbol = 'ATOM'
  GROUP BY date
),

token_prices AS (
SELECT
  s.date,
  NVL(l.project_name, s.denom) as symbol,
  s.atom_ratio,
  p.price,
  atom_ratio * IFF(price is not null, price, 0) AS price_usd
FROM (
SELECT 
  block_timestamp::date as date,
  CASE WHEN from_currency = 'ibc/27394FB092D2ECCD56123C74F36E4C1F926001CEADA9CA97EA622B25F41E5EB2'
  THEN to_currency
  ELSE from_currency
  END as denom,
  MEDIAN(
    CASE WHEN from_currency = 'ibc/27394FB092D2ECCD56123C74F36E4C1F926001CEADA9CA97EA622B25F41E5EB2'
    THEN (from_amount / POW(10, NVL(from_decimal, 6))) / (to_amount / POW(10, NVL(to_decimal, 6)))
    ELSE (to_amount / POW(10, NVL(to_decimal, 6))) / (from_amount / POW(10, NVL(from_decimal, 6)))
    END
  ) as atom_ratio
FROM osmosis.core.fact_swaps
WHERE 
  (from_currency = 'ibc/27394FB092D2ECCD56123C74F36E4C1F926001CEADA9CA97EA622B25F41E5EB2') OR 
  (to_currency = 'ibc/27394FB092D2ECCD56123C74F36E4C1F926001CEADA9CA97EA622B25F41E5EB2' AND block_timestamp >= '2022-06-21') -- get to only when after 2021-06-22 cause of inaccuracies in swap ratio
GROUP BY date, denom
) s
LEFT JOIN prices p
ON p.date = s.date
LEFT JOIN osmosis.core.dim_labels l
ON l.address = s.denom
),
  
pool_ids AS (
SELECT 
  block_timestamp as created_at,
  tx_id as pool_created_at_tx_id,
  MAX(IFF(attribute_key = 'pool_id', attribute_value, '')) as pool_id,
  MAX(IFF(attribute_key = 'recipient' AND LEN(attribute_value) > 43, attribute_value, '')) as pool_address
FROM osmosis.core.fact_msg_attributes
WHERE tx_id IN (
  SELECT tx_id
  FROM osmosis.core.fact_msg_attributes
  WHERE msg_type = 'pool_created'
)
GROUP BY block_timestamp, tx_id
),

mev_tx_id as (
  	select 1 from osmosis.core.fact_swaps s
	where from_currency = to_currency 
  	and tx_id = '{{tx_id}}'
  	limit 1
),
  
v2_swap AS (
  SELECT
  block_timestamp,
  tx_id,
  msg_group,
  msg_index,
  MAX(IFF(attribute_key = 'sender', attribute_value, '')) as swapper,
  MAX(IFF(attribute_key = 'pool_id', amount::integer, -1))::integer as pool_id,
  MAX(IFF(attribute_key = 'tokens_in', amount::integer, -1))::integer as from_amount,
  MAX(IFF(attribute_key = 'tokens_in', denom, '')) as from_denom,
  MAX(IFF(attribute_key = 'tokens_out', amount::integer, -1))::integer as to_amount,
  MAX(IFF(attribute_key = 'tokens_out', denom, '')) as to_denom
FROM (
  SELECT 
    block_timestamp,
    tx_id,
  	msg_group,
    msg_index,
  	attribute_key,
  	attribute_value,
    REGEXP_SUBSTR(attribute_value,'^\\\\d+') as amount,
    RIGHT(attribute_value, LEN(attribute_value) - LEN(amount::string)) as denom
  FROM osmosis.core.fact_msg_attributes att
  WHERE 
    msg_type = 'token_swapped'
  	AND tx_id = '{{tx_id}}'
  	and exists (select 1 from mev_tx_id)
)
GROUP BY
	block_timestamp,
	tx_id,
  	msg_group,
	msg_index
),

v1_swap as (
SELECT
  	block_timestamp,
  	tx_id,
    msg_group,
    msg_index,
  	swapper,
  	NVL(pool_id, s.pool_address) as pool_id,
  	from_amount,
  	from_denom,
  	to_amount,
  	to_denom
FROM (
  SELECT
      block_timestamp,
    	tx_id,
  		msg_group,
  		msg_index,
    	MAX(IFF(LEN(sender) = 43, sender, '')) as swapper,
    	MAX(IFF(LEN(sender) = 43, recipient, '')) as pool_address,
    	MAX(
    		IFF(
      		LEN(sender) = 43, 
      		amount::integer, 
      		-1
    		)
  	)::integer as from_amount,
    	MAX(IFF(LEN(sender) = 43, denom, '')) as from_denom,
    	MAX(
    		IFF(
      		LEN(sender) > 43, 
      		amount::integer, 
      		-1
    		)
  	)::integer as to_amount,
    	MAX(IFF(LEN(sender) > 43, denom, '')) as to_denom
  FROM (
    SELECT
    		block_timestamp,
    		tx_id,
    		sender,
    		recipient,
  			msg_group,
    		IFF(LEN(sender) = 43, LEAD(msg_index) OVER (PARTITION BY tx_id ORDER BY msg_index), msg_index) as msg_index, -- adjust to the corresponding msg_index to prevent complex tx getting overwritten
      		REGEXP_SUBSTR(amount_ori,'^\\\\d+') as amount,
    		RIGHT(amount_ori, LEN(amount_ori) - LEN(amount::string)) as denom
    FROM (
    		SELECT 
    			block_timestamp,
            	att.tx_id,
            	att.msg_index,
  				att.msg_group,
            	MAX(IFF(attribute_key = 'sender', attribute_value, '')) as sender,
            	MAX(IFF(attribute_key = 'recipient', attribute_value, '')) as recipient,
            	MAX(IFF(attribute_key = 'amount', attribute_value, '')) as amount_ori
    		FROM osmosis.core.fact_msg_attributes att
    		JOIN (
    			SELECT tx_id, msg_group, msg_index as action_index 
    			FROM osmosis.core.fact_msgs 
    			WHERE 
    				tx_status = 'SUCCEEDED' 
    				AND (msg:attributes[0]:value = 'c3dhcF9leGFjdF9hbW91bnRfaW4=' OR msg:attributes[0]:value = 'c3dhcF9leGFjdF9hbW91bnRfb3V0')
  					and tx_id = '{{tx_id}}'
  					and exists (select 1 from mev_tx_id)
          ) ms
    		ON ms.tx_id = att.tx_id AND ms.msg_group = att.msg_group
    	  	WHERE
              msg_type = 'transfer'
          GROUP BY
            block_timestamp,
            att.tx_id,
  			att.msg_group,
            att.msg_index
    		HAVING sender <> 'osmo1m5dncvfv7lvpvycr23zja93fecun2kcv226glq' -- claim contract
    )
  )
  GROUP BY
    block_timestamp,
    tx_id,
  	msg_group,
    msg_index
  ) s
  LEFT JOIN pool_ids i
  ON s.pool_address = i.pool_address
)

  
  SELECT 
  	a.*,
  	from_amount * p.price_usd as from_amount_usd,
  	to_amount * p2.price_usd as to_amount_usd
  FROM (
  SELECT 
    block_timestamp,
    tx_id,
    msg_group,
    msg_index,
    swapper,
    pool_id,
    from_amount / POW(10, NVL(l.raw_metadata[1]:exponent, 0)) as from_amount,
    NVL(l.project_name, from_denom) as from_denom,
    to_amount / POW(10, NVL(l2.raw_metadata[1]:exponent, 0)) as to_amount,
    NVL(l2.project_name, to_denom) as to_denom
  FROM (
  SELECT * FROM v2_swap
  UNION
  SELECT * FROM v1_swap
  ) v
  LEFT JOIN osmosis.core.dim_labels l
  ON l.address = from_denom
  LEFT JOIN osmosis.core.dim_labels l2
  ON l2.address = to_denom
  ) a
LEFT JOIN token_prices p
ON p.symbol = from_denom and p.date = block_timestamp::date
LEFT JOIN token_prices p2
ON p2.symbol = to_denom and p2.date = block_timestamp::date
ORDER BY msg_group, msg_index`;

const Tx = () => {
    const [pageData, setPageData] = useState<SpecificMEVTxDataResponse[]>([]);
    const [isLoading, setIsLoading] = useState(true);
	const { id } = useParams();

    const isQuerying = useRef(false);

    useEffect(() => {
        if(!id) {
            return;
        }

        if(isQuerying.current) {
            return;
        }

        isQuerying.current = true;

        const getData = async() => {
            let query = Mustache.render(txQuery, { tx_id: id});
            try {
                let res = await queryTillSuccess(query);
                let groups: SpecificMEVTxDataResponse[] = [];
                let group: SpecificMEVTxDataResponse = [];
                let groupIds: string[] = [];
                console.log(res);
                for(const data of res as SpecificMEVTxDataResponse) {
                    if(!groupIds.includes(data.msg_group)) {
                        if(group.length > 0) {
                            groups.push(group);
                        }

                        //reset data
                        groupIds.push(data.msg_group);
                        group = [];
                    }

                    group.push(data);
                }

                //leftovers
                if(group.length > 0) {
                    groups.push(group);
                }

                setPageData(groups);
            }

            catch(e) {
                console.log(e);
            }
            setIsLoading(false);
        }
        
        getData();
    }, [id]);

    const totalProfit = useMemo(() => {
        if(pageData.length === 0) {
            return 0;
        }

        let total = 0;
        for(const data of pageData) {
            total += data[data.length - 1].to_amount_usd - data[0].from_amount_usd;
        }

        return total;
    }, [pageData]);

    return (
        <div className="tx-page">
            <h1 className='mt-5'>MEV Flow</h1>
            {
                pageData.length > 0 &&
                <h2>Profit: ${toLocaleDecimal(totalProfit, 2, 2)}</h2>
            }
            {
                pageData.length > 0 &&
                pageData.map((data, index) => (

                    <div className="tx-details" key={`group-tx-details-${index}`}>
                        <strong>MEV #{index + 1}</strong>
                        <div className='tx-detail'>
                            <div>{data[0].from_denom}</div>
                            <div>{toLocaleDecimal(data[0].from_amount, 2, 2)}</div>
                            <div>${toLocaleDecimal(data[0].from_amount_usd, 2, 2)}</div>
                        </div>
                        {
                            data.map((x, innerIndex) => {
                                if(innerIndex === 0) {
                                    return null;
                                }

                                return (
                                    <React.Fragment key={`tx-details-index-${innerIndex}-${index}`}>
                                        <div className='arrow-container'>
                                            Pool #{x.pool_id}
                                            <i className="mdi mdi-arrow-right-bold"></i>
                                        </div>
                                        <div className='tx-detail'>
                                            <div>{x.to_denom}</div>
                                            <div>{toLocaleDecimal(x.to_amount, 2, 2)}</div>
                                            <div>${toLocaleDecimal(x.to_amount_usd, 2, 2)}</div>
                                        </div>
                                    </React.Fragment>     
                                );       
                            })
                    }
                    </div>

                ))
            }

            <Spinner
                type='pulse'
                mode="dark"
                text="Loading"
                show={isLoading}
                fullScreen
            />
        </div>
    );
}

export default Tx;